import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Check, Loader2, ArrowLeft } from 'lucide-react'
import { Button, Card, CardContent, Input, Select, type SelectOption } from '@/components/ui'
import { getVehicleById } from '@/data/vehicles'
import { formatPrice, formatMileage, calculateMonthlyPayment } from '@/utils/format'

type PurchaseStep = 'review' | 'financing' | 'contact' | 'confirm'

const termOptions: SelectOption[] = [
  { value: '36', label: '36 months' },
  { value: '48', label: '48 months' },
  { value: '60', label: '60 months' },
  { value: '72', label: '72 months' },
  { value: '84', label: '84 months' },
]

export function PurchasePage() {
  const { vehicleId } = useParams<{ vehicleId: string }>()
  const vehicle = vehicleId ? getVehicleById(vehicleId) : null

  const [step, setStep] = useState<PurchaseStep>('review')
  const [submitting, setSubmitting] = useState(false)

  // Financing state
  const [downPayment, setDownPayment] = useState('5000')
  const [termMonths, setTermMonths] = useState(60)

  // Contact state
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })

  const monthlyPayment = useMemo(() => {
    if (!vehicle) return 0
    const downPaymentCents = parseInt(downPayment || '0') * 100
    return calculateMonthlyPayment(vehicle.priceCents, downPaymentCents, 6.99, termMonths)
  }, [vehicle, downPayment, termMonths])

  const handleSubmit = async () => {
    setSubmitting(true)
    await new Promise((resolve) => setTimeout(resolve, 1500))
    setSubmitting(false)
    setStep('confirm')
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto text-center">
          <CardContent className="py-8">
            <h1 className="text-2xl font-bold text-gray-900">Vehicle Not Found</h1>
            <p className="mt-2 text-gray-600">
              This vehicle may no longer be available.
            </p>
            <Link to="/vehicles" className="inline-block mt-6">
              <Button>Browse Available Vehicles</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  const title = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`

  if (step === 'confirm') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="mt-6 text-2xl font-bold text-gray-900">
                Purchase Request Submitted!
              </h1>
              <p className="mt-2 text-gray-600">
                Thank you for your interest in the {title}. Our team will contact
                you within 24 hours to complete your purchase.
              </p>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left">
                <h3 className="font-semibold text-gray-900">Next Steps:</h3>
                <ul className="mt-2 space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-600 flex-shrink-0">
                      1
                    </span>
                    Our sales team will review your request
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-600 flex-shrink-0">
                      2
                    </span>
                    You'll receive a call or email within 24 hours
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-600 flex-shrink-0">
                      3
                    </span>
                    Schedule a test drive or complete financing
                  </li>
                </ul>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/account">
                  <Button>View My Account</Button>
                </Link>
                <Link to="/vehicles">
                  <Button variant="outline">Continue Shopping</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Link
          to={`/vehicles/${vehicle.id}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Vehicle
        </Link>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Start Your Purchase</h1>
          <p className="mt-2 text-gray-600">
            Complete the steps below to begin the purchase process
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md">
            {(['review', 'financing', 'contact'] as const).map((s, index) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step === s
                      ? 'bg-primary-600 text-white'
                      : index < ['review', 'financing', 'contact'].indexOf(step)
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index < ['review', 'financing', 'contact'].indexOf(step) ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                {index < 2 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      index < ['review', 'financing', 'contact'].indexOf(step)
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between max-w-md text-xs text-gray-600 mt-2">
            <span>Review</span>
            <span>Financing</span>
            <span>Contact</span>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 'review' && (
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Review Your Vehicle
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="w-full sm:w-48 aspect-video rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {vehicle.photos[0] && (
                        <img
                          src={vehicle.photos[0]}
                          alt={title}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {title}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">VIN: {vehicle.vin}</p>
                      <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <dt className="text-gray-500">Mileage</dt>
                          <dd className="font-medium text-gray-900">
                            {formatMileage(vehicle.mileage)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Exterior</dt>
                          <dd className="font-medium text-gray-900">
                            {vehicle.exteriorColor}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Transmission</dt>
                          <dd className="font-medium text-gray-900 capitalize">
                            {vehicle.transmission}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Fuel Type</dt>
                          <dd className="font-medium text-gray-900 capitalize">
                            {vehicle.fuelType}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <Button onClick={() => setStep('financing')}>
                      Continue to Financing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {step === 'financing' && (
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Financing Options
                  </h2>
                  <div className="space-y-6">
                    <Input
                      label="Down Payment ($)"
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(e.target.value)}
                      min={0}
                    />
                    <Select
                      label="Loan Term"
                      options={termOptions}
                      value={termMonths.toString()}
                      onChange={(e) => setTermMonths(parseInt(e.target.value))}
                    />
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Estimated APR:{' '}
                        <span className="font-medium text-gray-900">6.99%</span>
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Final APR based on credit approval
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-between">
                    <Button variant="outline" onClick={() => setStep('review')}>
                      Back
                    </Button>
                    <Button onClick={() => setStep('contact')}>
                      Continue to Contact Info
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {step === 'contact' && (
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Contact Information
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="First Name"
                      value={contactInfo.firstName}
                      onChange={(e) =>
                        setContactInfo({ ...contactInfo, firstName: e.target.value })
                      }
                      required
                    />
                    <Input
                      label="Last Name"
                      value={contactInfo.lastName}
                      onChange={(e) =>
                        setContactInfo({ ...contactInfo, lastName: e.target.value })
                      }
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) =>
                        setContactInfo({ ...contactInfo, email: e.target.value })
                      }
                      required
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      value={contactInfo.phone}
                      onChange={(e) =>
                        setContactInfo({ ...contactInfo, phone: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="mt-6 flex justify-between">
                    <Button variant="outline" onClick={() => setStep('financing')}>
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={
                        submitting ||
                        !contactInfo.firstName ||
                        !contactInfo.lastName ||
                        !contactInfo.email ||
                        !contactInfo.phone
                      }
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Purchase Request'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Order Summary */}
          <div>
            <Card className="sticky top-20">
              <CardContent>
                <h2 className="font-semibold text-gray-900 mb-4">Order Summary</h2>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vehicle Price</span>
                    <span className="font-medium text-gray-900">
                      {formatPrice(vehicle.priceCents)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment</span>
                    <span className="font-medium text-gray-900">
                      -{formatPrice(parseInt(downPayment || '0') * 100)}
                    </span>
                  </div>
                  <hr className="border-gray-200" />
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Financed</span>
                    <span className="font-medium text-gray-900">
                      {formatPrice(
                        vehicle.priceCents - parseInt(downPayment || '0') * 100
                      )}
                    </span>
                  </div>
                  <div className="p-4 bg-primary-50 rounded-lg">
                    <p className="text-sm text-gray-600">Estimated Monthly Payment</p>
                    <p className="text-2xl font-bold text-primary-600">
                      {formatPrice(monthlyPayment)}
                      <span className="text-sm font-normal text-gray-600">/mo</span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      for {termMonths} months at 6.99% APR
                    </p>
                  </div>
                </div>
                <div className="mt-6 p-4 rounded-lg border border-gray-200 text-sm text-gray-600">
                  <p className="flex items-start gap-2">
                    Final terms subject to credit approval. Additional fees may apply.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
